// src/personel-takip/src/pages/BelgeYonetimi.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { db, auth } from "../firebaseConfig";
import {
  collection,
  addDoc,
  getDocs,
  serverTimestamp,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import Select from "react-select";

/* ========== Helpers ========== */
const U = (s = "") => s.trim().toLocaleUpperCase("tr-TR");
const byLocale = (a, b) => (a || "").localeCompare(b || "", "tr", { sensitivity: "base" });

/*
personelDocTypes: {
  name: string,
  rolesRequired: string[],                // UPPERCASE
  validityMonths: number,
  roleValidityMonths?: { [ROLE]: number },
  warnDay?: number,                       // TEK SAYI (yeni)
  warnDays?: number[],                    // Eski kayıtlar için kalabilir
  createdAt, createdBy
}
*/
const BelgeYonetimi = () => {
  // Kaynaklar
  const [personeller, setPersoneller] = useState([]);
  const [docTypes, setDocTypes] = useState([]);

  useEffect(() => {
    (async () => {
      const [pSnap, tSnap] = await Promise.all([
        getDocs(collection(db, "personeller")),
        getDocs(collection(db, "personelDocTypes")),
      ]);
      setPersoneller(pSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setDocTypes(tSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    })();
  }, []);

  // Görev seçenekleri (personellerden türetilir)
  const roleOptions = useMemo(() => {
    const s = new Set();
    personeller.forEach((p) => p.gorev && s.add(U(p.gorev)));
    return Array.from(s)
      .sort(byLocale)
      .map((r) => ({ value: r, label: r }));
  }, [personeller]);

  /* ---------- 1) Belge Tipi formu ---------- */
  const [dtName, setDtName] = useState("");
  const [dtValidity, setDtValidity] = useState(12);
  const [dtRenewOnRehire, setDtRenewOnRehire] = useState(false);
  const [dtAllowMultiple, setDtAllowMultiple] = useState(false);
  const [dtScopeLabel, setDtScopeLabel] = useState("Bölge/Tesis");
  const [dtWarnDay, setDtWarnDay] = useState(7); // tek sayı

  // Düzenleme modu
  const [editingDocTypeId, setEditingDocTypeId] = useState("");

  // react-select çoklu seçim (zorunlu roller)
  const [dtSelectedRoles, setDtSelectedRoles] = useState([]); // [{value,label}]

  // Rol bazlı süre satırları
  const [dtRoleMonthsRows, setDtRoleMonthsRows] = useState([]); // [{role, months}]
  const addRoleMonthRow = () => {
    const remaining = roleOptions
      .map((o) => o.value)
      .filter((r) => !dtRoleMonthsRows.some((x) => x.role === r));
    setDtRoleMonthsRows((prev) => [...prev, { role: remaining[0] || "", months: 12 }]);
  };
  const updateRoleMonth = (idx, key, val) => {
    setDtRoleMonthsRows((prev) =>
      prev.map((row, i) => {
        if (i !== idx) return row;
        if (key === "role") return { ...row, role: val };
        if (key === "months")
          return { ...row, months: Math.max(1, parseInt(val || "1", 10) || 1) };
        return row;
      })
    );
  };
  const removeRoleMonthRow = (idx) => {
    setDtRoleMonthsRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const resetDocTypeForm = () => {
    setEditingDocTypeId("");
    setDtName("");
    setDtValidity(12);
    setDtWarnDay(7);
    setDtRenewOnRehire(false);
    setDtAllowMultiple(false);
    setDtScopeLabel("Bölge/Tesis");
    setDtSelectedRoles([]);
    setDtRoleMonthsRows([]);
  };

  const saveDocType = async () => {
    const name = dtName.trim();
    if (!name) return alert("Belge adı girin.");
    if (!dtValidity || isNaN(parseInt(dtValidity, 10)))
      return alert("Geçerlilik (Ay) sayısal olmalı.");
    if (!dtWarnDay || isNaN(parseInt(dtWarnDay, 10)))
      return alert("Uyarı Günü sayısal olmalı.");

    const cleanRoleMonths = {};
    dtRoleMonthsRows.forEach((r) => {
      const role = U(r.role || "");
      const months = parseInt(r.months, 10);
      if (role && !isNaN(months) && months > 0) cleanRoleMonths[role] = months;
    });

    const payload = {
      name,
      rolesRequired: dtSelectedRoles.map((o) => o.value), // UPPERCASE array
      validityMonths: parseInt(dtValidity, 10),
      renewOnRehire: !!dtRenewOnRehire,
      allowMultiple: !!dtAllowMultiple,
      multipleScopeLabel: dtAllowMultiple ? (dtScopeLabel.trim() || "Bölge/Tesis") : "",
      warnDay: parseInt(dtWarnDay, 10),                   // tek sayı
      ...(Object.keys(cleanRoleMonths).length
        ? { roleValidityMonths: cleanRoleMonths }
        : {}),
      createdBy: auth?.currentUser?.email || "anonim",
      createdAt: serverTimestamp(),
    };

    if (editingDocTypeId) {
      await updateDoc(doc(db, "personelDocTypes", editingDocTypeId), payload);
      setDocTypes((prev) =>
        prev.map((t) => (t.id === editingDocTypeId ? { id: t.id, ...payload } : t))
      );
      alert("Belge tipi güncellendi ✅");
    } else {
      const ref = await addDoc(collection(db, "personelDocTypes"), payload);
      setDocTypes((prev) => [...prev, { id: ref.id, ...payload }]);
      alert("Belge tipi kaydedildi ✅");
    }
    resetDocTypeForm();
  };

  const editDocType = (t) => {
    setEditingDocTypeId(t.id);
    setDtName(t.name || "");
    setDtValidity(t.validityMonths || 12);
    setDtRenewOnRehire(!!t.renewOnRehire);
    setDtAllowMultiple(!!t.allowMultiple);
    setDtScopeLabel(t.multipleScopeLabel || "Bölge/Tesis");
    setDtWarnDay(Number.isFinite(t.warnDay) ? t.warnDay : (t.warnDays?.length ? Math.min(...t.warnDays) : 7));
    setDtSelectedRoles((t.rolesRequired || []).map((r) => ({ value: r, label: r })));
    const rows = t.roleValidityMonths
      ? Object.entries(t.roleValidityMonths).map(([role, months]) => ({ role, months }))
      : [];
    setDtRoleMonthsRows(rows);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteDocType = async (t) => {
    if (!window.confirm(`"${t.name}" belge tipini silmek istediğine emin misin?`)) return;
    await deleteDoc(doc(db, "personelDocTypes", t.id));
    setDocTypes((prev) => prev.filter((x) => x.id !== t.id));
  };

  // Görünüm filtreleri
  const [searchDocType, setSearchDocType] = useState("");
  const [searchRole, setSearchRole] = useState("");
  const docTypesFiltered = useMemo(() => {
    return docTypes
      .filter(
        (t) =>
          !searchDocType ||
          (t.name || "").toLowerCase().includes(searchDocType.toLowerCase())
      )
      .filter(
        (t) =>
          !searchRole ||
          (t.rolesRequired || []).some((r) => r.includes(U(searchRole)))
      )
      .sort((a, b) => byLocale(a.name, b.name));
  }, [docTypes, searchDocType, searchRole]);

  return (
    <div className="wrap">
      <style>{`
        .wrap{ padding:16px; font-family:system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue",Arial; }
        .grid2{ display:grid; grid-template-columns: 1fr 1fr; gap:12px; }
        .grid3{ display:grid; grid-template-columns: 1fr 1fr 1fr; gap:12px; }
        @media (max-width: 980px){ .grid2,.grid3 { grid-template-columns: 1fr; } }
        .card{ border:1px solid #e5e7eb; border-radius:10px; padding:14px; margin-bottom:16px; background:#fff; }
        .title{ font-weight:700; margin-bottom:10px; }
        .muted{ color:#6b7280; font-size:12px; }
        .btn{ padding:9px 12px; border-radius:8px; border:1px solid #d1d5db; background:#111827; color:#fff; font-weight:600; cursor:pointer; }
        .btn.light{ background:#fff; color:#111827; }
        input, select, textarea { padding:8px 10px; border:1px solid #d1d5db; border-radius:8px; font-size:13px; width:100%; box-sizing:border-box; }
        table{ width:100%; border-collapse:collapse; font-size:13px; }
        th, td{ border:1px solid #e5e7eb; padding:8px; text-align:left; vertical-align:top; }
        thead th{ background:#f8fafc; }
      `}</style>

      <h2>Belge Yönetimi</h2>

      {/* 1) Belge Tipi Tanımla */}
      <div className="card">
        <div className="title">1) Belge Tipi Tanımla</div>

        <div className="grid3" style={{ marginBottom: 8 }}>
          <div>
            <div className="muted">Belge Adı</div>
            <input placeholder="Örn: Psikoteknik" value={dtName} onChange={(e) => setDtName(e.target.value)} />
          </div>

          <div>
            <div className="muted">Genel Geçerlilik (Ay)</div>
            <input type="number" min={1} value={dtValidity} onChange={(e) => setDtValidity(e.target.value)} />
          </div>

          <div>
            <div className="muted">Uyarı Günü (tek sayı)</div>
            <input type="number" min={1} value={dtWarnDay} onChange={(e) => setDtWarnDay(e.target.value)} />
          </div>
        </div>

        <div style={{ marginTop: 8 }}>
          <div className="muted" style={{ marginBottom: 6 }}>Zorunlu Görevler (çoklu)</div>
          <Select
            options={roleOptions}
            isMulti
            isSearchable
            closeMenuOnSelect={false}
            placeholder="Görev seç..."
            value={dtSelectedRoles}
            onChange={(multi) => setDtSelectedRoles(multi || [])}
          />
        </div>

        {/* Rol Bazlı Süre (opsiyonel) */}
        <div className="grid3" style={{ marginTop: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={dtRenewOnRehire}
              onChange={(e) => setDtRenewOnRehire(e.target.checked)}
              style={{ width: "auto" }}
            />
            <span>Tekrar işe girişte yeniden istenir</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={dtAllowMultiple}
              onChange={(e) => setDtAllowMultiple(e.target.checked)}
              style={{ width: "auto" }}
            />
            <span>Aynı tipten birden fazla yüklenebilir</span>
          </label>
          <div>
            <div className="muted">Çoklu belge etiketi</div>
            <input
              value={dtScopeLabel}
              onChange={(e) => setDtScopeLabel(e.target.value)}
              disabled={!dtAllowMultiple}
              placeholder="Örn: Bölge/Tesis"
            />
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <div className="muted" style={{ marginBottom: 6 }}>Rol Bazlı Geçerlilik (Ay) — opsiyonel</div>
          <div style={{ marginBottom: 8 }}>
            <button className="btn light" onClick={addRoleMonthRow}>+ Satır Ekle</button>
          </div>
          {dtRoleMonthsRows.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>Görev</th>
                  <th>Ay</th>
                  <th>Sil</th>
                </tr>
              </thead>
              <tbody>
                {dtRoleMonthsRows.map((row, idx) => (
                  <tr key={idx}>
                    <td>
                      <select
                        value={row.role}
                        onChange={(e) => updateRoleMonth(idx, "role", e.target.value)}
                      >
                        <option value="">(Seçin)</option>
                        {roleOptions.map((o) => (
                          <option
                            key={o.value}
                            value={o.value}
                            disabled={dtRoleMonthsRows.some((x, i) => x.role === o.value && i !== idx)}
                          >
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ maxWidth: 120 }}>
                      <input
                        type="number"
                        min={1}
                        value={row.months}
                        onChange={(e) => updateRoleMonth(idx, "months", e.target.value)}
                      />
                    </td>
                    <td style={{ width: 80 }}>
                      <button className="btn light" onClick={() => removeRoleMonthRow(idx)}>Sil</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!!dtRoleMonthsRows.length && (
            <div className="muted" style={{ marginTop: 6 }}>
              Not: Buradaki süreler ilgili rol için <b>genel sürenin yerine</b> uygulanır.
            </div>
          )}
        </div>

        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
          <button className="btn" onClick={saveDocType}>
            {editingDocTypeId ? "Belge Tipini Güncelle" : "Belge Tipini Kaydet"}
          </button>
          {editingDocTypeId && (
            <button className="btn light" onClick={resetDocTypeForm}>İptal</button>
          )}
        </div>
      </div>

      {/* 2) Tanımlı Evrak Tipleri */}
      <div className="card">
        <div className="title">2) Tanımlı Evrak Tipleri</div>

        <div className="grid2" style={{ marginBottom: 8 }}>
          <input
            placeholder="Belge tipi ara..."
            value={searchDocType}
            onChange={(e) => setSearchDocType(e.target.value)}
          />
          <input
            placeholder="Görev ara (örn: OPERATÖR)"
            value={searchRole}
            onChange={(e) => setSearchRole(e.target.value)}
          />
        </div>

        <table>
          <thead>
            <tr>
              <th>Belge</th>
              <th>Zorunlu Görevler</th>
              <th>Genel Ay</th>
              <th>Yeniden Giriş</th>
              <th>Çoklu</th>
              <th>Rol Bazlı Ay</th>
              <th>Uyarı Günü</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {docTypesFiltered.map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td>{(t.rolesRequired || []).join(", ") || "—"}</td>
                <td>{t.validityMonths || "—"}</td>
                <td>{t.renewOnRehire ? "Evet" : "Hayır"}</td>
                <td>{t.allowMultiple ? (t.multipleScopeLabel || "Evet") : "Hayır"}</td>
                <td>
                  {t.roleValidityMonths
                    ? Object.entries(t.roleValidityMonths)
                        .sort(([a], [b]) => byLocale(a, b))
                        .map(([role, m]) => `${role}: ${m}`)
                        .join(", ")
                    : "—"}
                </td>
                <td>{Number.isFinite(t.warnDay) ? t.warnDay : (t.warnDays?.length ? Math.min(...t.warnDays) : "—")}</td>
                <td>
                  <div style={{ display:"flex", gap:8 }}>
                    <button className="btn light" onClick={() => editDocType(t)}>Düzenle</button>
                    <button className="btn light" onClick={() => deleteDocType(t)}>Sil</button>
                  </div>
                </td>
              </tr>
            ))}
            {!docTypesFiltered.length && (
              <tr>
                <td colSpan={6} style={{ color: "#6b7280" }}>(Kayıt yok)</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BelgeYonetimi;
